const post = {
  "slug": "public-catalog-page-when-it-updates",
  "title": "Public Catalog Page: What Updates It and What Does Not",
  "seoTitle": "Public Catalog Page: When It Updates",
  "description": "Your Public Catalog Page is a saved snapshot, not a live view. Learn what shows on each plan, which edits refresh it and what to do when a change is missing.",
  "excerpt": "If a product, photo or coupon is missing from your shareable catalog link, the cause is almost always the snapshot. Here is what rebuilds it and what does not.",
  "category": "Catalog",
  "tags": [
    "Public Catalog Page",
    "Instagram storefront",
    "Catalog link",
    "Checkout DM catalog"
  ],
  "date": "2026-11-15",
  "cover": {
    "src": "/blog-images/public-catalog-page-when-it-updates-cover.png",
    "alt": "A shareable storefront link opening a read-only catalog of products with a New badge"
  },
  "blocks": [
    {
      "type": "p",
      "text": "The Public Catalog Page gives you one link to your whole catalog, which you can post in your Instagram bio, a WhatsApp status or a Linktree. Customers can browse your products and add them to a cart, but every order still finishes inside your Instagram DMs, exactly as it does today. The detail that trips people up is that the page is a saved snapshot, not a live view. This guide explains when the snapshot updates."
    },
    {
      "type": "h2",
      "text": "Your link and who gets it"
    },
    {
      "type": "p",
      "text": "The page lives at checkoutdm.com/store/ followed by your Instagram username, the same username connected to your account, so there is nothing extra to set up. It is available on Creator and above. On the Free plan, the toggle is not shown, and the link returns nothing even if someone guesses the address. To publish it, switch on the toggle on the card at the top of your Catalog screen. Turning it on for the first time builds the snapshot immediately, so the page is live with current data straight away."
    },
    {
      "type": "h2",
      "text": "What a snapshot means"
    },
    {
      "type": "p",
      "text": "A background job pre-builds a single saved copy of your store: products, pricing and, depending on your plan, branding, social links and active discounts. Every visitor sees that saved copy, which is what keeps the page fast under heavy traffic. The trade-off is that changes in your dashboard do not appear until something rebuilds the snapshot. There is no timer that refreshes it by itself."
    },
    {
      "type": "h2",
      "text": "What shows on each plan"
    },
    {
      "type": "p",
      "text": "The snapshot only includes what your plan is entitled to, decided at the moment it is built."
    },
    {
      "type": "table",
      "headers": [
        "Plan",
        "What the page includes"
      ],
      "rows": [
        [
          "Creator",
          "Digital products (name, price, description, main photo, category) with extended descriptions and FAQs on a basic storefront. No business address footer, extra photos or coupons."
        ],
        [
          "Starter",
          "Everything in Creator, plus physical products, 1 extra photo per product (2 in total with the main image) and your business address in the footer."
        ],
        [
          "Growth",
          "Everything in Starter, plus a second extra photo (3 in total), your social media links and any active coupons or discounts."
        ],
        [
          "Pro",
          "Everything in Growth, plus a third extra photo (4 in total), your logo, footer tagline, website link and phone number in the footer."
        ]
      ]
    },
    {
      "type": "h2",
      "text": "What rebuilds the snapshot"
    },
    {
      "type": "ul",
      "items": [
        "**Adding a product:** always rebuilds, whether you add one at a time or use Pro\u2019s bulk photo upload.",
        "**Deleting a product:** always rebuilds.",
        "**Editing a product:** rebuilds only if the price or the main photo changed. Editing just the name, the description or a Pro merchant\u2019s additional photos does not rebuild it on its own.",
        "**Turning the toggle on:** rebuilds once, at the moment you switch it from off to on.",
        "**Turning the toggle off or changing other settings:** rebuilds nothing, because the page stops serving once it is off."
      ]
    },
    {
      "type": "callout",
      "tone": "peach",
      "title": "The most common cause of a missing photo",
      "text": "If you added extra photos to a product and cannot see them on the page, this is why: editing additional photos alone does not rebuild the snapshot. Make a small price change and change it back, or re-save the main photo. You can also ask Checkout DM to trigger a manual rebuild for your store."
    },
    {
      "type": "h2",
      "text": "The New badge"
    },
    {
      "type": "p",
      "text": "A product shows a New badge for exactly 14 days from the date it was first added to your catalog, not from the last time you edited it. Unlike everything else on the page, the badge is calculated fresh in the visitor\u2019s browser each time the page loads. It therefore disappears on schedule even if the snapshot has not been rebuilt for a month, and you never need to trigger a rebuild to remove an old badge."
    },
    {
      "type": "h2",
      "text": "Coupons on the page"
    },
    {
      "type": "p",
      "text": "Coupons show on the page on Growth and Pro. They are saved into the snapshot like products, so creating, editing, deleting or toggling a coupon does not rebuild it by itself. A brand-new coupon will not appear until the next rebuild happens for another reason. A time-based coupon that has expired can also keep showing as active until then, because its expiry is only checked when the snapshot is built. It is still rejected if a customer tries to use it at checkout in DMs. Our guide to [discount codes and free shipping for Instagram orders](/blog/discount-codes-and-free-shipping-for-instagram-orders) covers how coupons work."
    },
    {
      "type": "h2",
      "text": "If the page shows nothing"
    },
    {
      "type": "p",
      "text": "A blank page or a 404 usually means one of two things: the toggle is off, or your plan does not include the feature (the Free plan). Check the toggle on your Catalog screen first."
    },
    {
      "type": "p",
      "text": "The Public Catalog Page is one way to let people see your products before they message you. If you do not have a website at all, our guide to [selling on Instagram without a website](/blog/sell-on-instagram-without-a-website) shows how the pieces fit together."
    }
  ],
  "related": [
    {
      "to": "/public-catalog-guide",
      "title": "Public Catalog Page Guide",
      "description": "The full explanation of what shows and when it refreshes."
    },
    {
      "to": "/catalog-guide",
      "title": "Catalog Guide",
      "description": "What every part of the Catalog screen means."
    },
    {
      "to": "/pricing",
      "title": "Pricing",
      "description": "Compare what each plan shows on the page."
    }
  ],
  "faqs": [
    {
      "q": "Where is my Public Catalog Page?",
      "a": "At checkoutdm.com/store/ followed by your connected Instagram username. Switch it on from the toggle at the top of your Catalog screen."
    },
    {
      "q": "Is the Public Catalog Page available on the Free plan?",
      "a": "No. It is available on Creator and above. On Free the toggle is not shown and the link returns nothing."
    },
    {
      "q": "Why is my product edit not showing on the page?",
      "a": "Editing a product only rebuilds the snapshot if the price or main photo changed. Changes to the name, description or additional photos alone do not rebuild it."
    },
    {
      "q": "How long does the New badge last?",
      "a": "14 days from when the product was first added, calculated live in the visitor\u2019s browser."
    },
    {
      "q": "Can customers check out on the page?",
      "a": "They can browse and add products to a cart, but every order still finishes inside your Instagram DMs."
    }
  ]
};

export default post;
